import {
  CaretDownOutlined,
  CaretRightOutlined,
  EllipsisOutlined,
  LeftOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Select,
  Spin,
  Table,
  Tooltip,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import _, { debounce, delay, forEach, uniqBy } from "lodash";
import moment from "moment";
import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BiDetail } from "react-icons/bi";
import { IoIosAddCircle } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import { MdDataSaverOn, MdDelete, MdOutlineTimeline } from "react-icons/md";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../../api";
import { CustomAvatar } from "../../../components/avatar/CustomAvatar";
import { GeneralModal } from "../../../components/modal/GeneralModal";
import { MemberProjectModal } from "../../../components/modal/MemberProjectModal";
import SideBarTimeline from "../../../components/sideBar/SideBarTimeline";
import {
  AUTHORITY_CONFIG,
  MY_TASK,
  PROJECT_VIEW_MODE,
} from "../../../config/Constant";
import { useSideBarStore } from "../../../store/SideBarStore";
import { useInfoUser } from "../../../store/UserStore";
import "../../../styles/table.scss";
import {
  getUserPermissions,
  openNotificationError,
  openNotificationSuccess,
} from "../../../utils/Utils";
import MobileCardTable from "./MobileCardTable";
import "./style.scss";

const DragIndexContext = createContext({
  active: -1,
  over: -1,
});

const TableHeaderCell = (props) => {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props?.id,
  });

  const style = {
    ...props?.style,
    textAlign: "center",
    cursor: "default",
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 10,
          userSelect: "none",
        }
      : {}),
  };

  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

const Row = forwardRef((props, ref) => {
  const record = props?.children?.[0]?.props?.record;

  const isDraggable =
    record?.taskCode && record?.path?.split("|")?.length - 1 === 0;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: isDraggable ? "move" : "default",
    ...(isDragging && isDraggable
      ? {
          position: "relative",
          zIndex: 0,
        }
      : {}),
  };

  return (
    <tr
      {...props}
      ref={(node) => {
        setNodeRef(node);
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      style={style}
      {...attributes}
      {...(isDraggable ? listeners : {})}
    />
  );
});

export const TaskNameInput = memo(
  ({ languageMap, item, onBlurName, disabled, isMobile }) => {
    const [value, setValue] = useState(item?.name || "");

    useEffect(() => {
      setValue(item?.name || "");
    }, [item]);

    const handleChange = (e) => {
      setValue(e?.target?.value);
    };

    const handleBlur = (e) => {
      onBlurName?.(item, value);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") handleBlur(e);
    };

    const handlePreventAllEvents = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <>
        {isMobile ? (
          <div className="flex w-full items-center justify-between">
            <TextArea
              className="task-name flex-1 mr-2"
              autoFocus
              maxLength={100}
              value={value}
              onChange={handleChange}
              onDoubleClick={handlePreventAllEvents}
              onKeyDown={handleKeyDown}
              autoSize={{ minRows: 1, maxRows: 5 }}
              placeholder="Task name"
              disabled={disabled}
            />
            <Button
              icon={<MdDataSaverOn color={"white"} />}
              type="primary"
              size="small"
              onClick={handleBlur}
              disabled={!value?.trim() || disabled}
              style={{
                backgroundColor: !value?.trim() ? "#ccc" : "#52c41a",
                color: "white",
              }}
            >
              {languageMap?.["timeline.mobile.buttonSave"] ?? "Lưu"}
            </Button>
          </div>
        ) : (
          <TextArea
            className="task-name w-full"
            autoFocus
            maxLength={100}
            value={value}
            onChange={handleChange}
            onDoubleClick={handlePreventAllEvents}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoSize={{ minRows: 1, maxRows: 5 }}
            placeholder="Task name"
            disabled={disabled}
          />
        )}
      </>
    );
  }
);

export const StatusSelect = memo(
  ({ item, options, onChangeStatus, disabled }) => {
    const handlePreventAllEvents = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <Select
        value={item?.workflowCode || null}
        className="w-full"
        options={options}
        onDoubleClick={handlePreventAllEvents}
        onChange={(value) => onChangeStatus?.(value, item)}
        disabled={disabled}
      />
    );
  }
);

export const ProgressInput = memo(({ item, onChangeProgress, disabled }) => {
  const [value, setValue] = useState(item?.progressRate || 0);

  useEffect(() => {
    setValue(item?.progressRate || 0);
  }, [item?.progressRate]);

  const handleChange = (value) => {
    const regex = /^\d*$/;

    if (regex.test(value)) {
      if (value < 0) {
        setValue(0);
      } else if (value > 100) {
        setValue(100);
      } else setValue(value);
    }
  };

  const handleBlur = () => {
    onChangeProgress?.(item, value);
  };

  const handlePreventAllEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <InputNumber
      className="w-full"
      value={value}
      onDoubleClick={handlePreventAllEvents}
      onChange={handleChange}
      onBlur={handleBlur}
      min={0}
      max={100}
      step={5}
      disabled={disabled}
      formatter={(value) => `${value}%`}
      parser={(value) => value.replace("%", "")}
    />
  );
});

export const DateInput = memo(
  ({ item, type, onChangeDate, placeholder, disabled }) => {
    const handlePreventAllEvents = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const value = item?.[type] && dayjs(item?.[type], "YYYY-MM-DD");
    const minDate =
      type === "endDate" && item?.startDate
        ? dayjs(item?.startDate, "YYYY-MM-DD")
        : null;
    const maxDate =
      type === "startDate" && item?.endDate
        ? dayjs(item?.endDate, "YYYY-MM-DD")
        : null;

    return (
      <DatePicker
        value={value}
        onChange={(date, dateString) =>
          onChangeDate?.(dateString, type, item, date)
        }
        onDoubleClick={handlePreventAllEvents}
        placeholder={placeholder}
        readOnly
        format="YYYY/MM/DD"
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
      />
    );
  }
);

const Timeline = () => {
  const { projectCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectDetail, setProjectDetail] = useState(null);
  const [timelineType, setTimelineType] = useState("date");
  const [amplitude, setAmplitude] = useState(12);
  const [viewMode, setViewMode] = useState(PROJECT_VIEW_MODE.DETAIL);
  const [timelineLength, setTimelineLength] = useState(0);
  const [todayTimelineLength, setTodayTimelineLength] = useState(0);
  const tableRef = useRef(null);
  const headerDiv = useRef(null);
  const expandTinelineLength = 650;
  let numberOfWeek = 0;
  const [timelineHeight, setTimelineHeight] = useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [sortSequence, setSortSequence] = useState(null);
  const [data, setData] = useState([]);
  const [unwindTaskList, setUnwindTaskList] = useState([]);
  const [projectProgressListSelect, setProjectProgressListSelect] = useState(
    []
  );
  const [projectProgressList, setProjectProgressList] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const { user, languageMap } = useInfoUser();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const [deletionTask, setDeletionTask] = useState(null);
  const [isOpenTimelineDetail, setIsOpenTimelineDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState({});
  const [isModalAddMember, setIsModalAddMember] = useState(false);
  const [typeMemberProjectModal, setTypeMemberProjectModal] =
    useState("CREATE");
  const [isNewRecord, setIsNewRecord] = useState(false);
  const navigate = useNavigate();
  const [dragIndex, setDragIndex] = useState({
    active: -1,
    over: -1,
  });

  const [dataSource, setDataSource] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoadMoreData, setIsLoadMoreData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionCurrentUser, setPermissionCurrentUser] = useState(null);
  const [rowChangeStylePaths, setRowChangeStylePaths] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [oldScrollY, setOldScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 860);

  const handleResizeSc = () => {
    setIsMobile(window.innerWidth < 860);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResizeSc);
    return () => {
      window.removeEventListener("resize", handleResizeSc);
    };
  }, []);

  const { switchIsWorkManagementOptions, isWorkManagementOptions } =
    useSideBarStore((state) => state);

  const lastObserver = useRef();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const timelineColumnList = useMemo(
    () => [
      {
        title:
          languageMap?.["event.dashboard.myProject.timeline.name"] ?? "Name",
        dataIndex: "name",
        key: "name",
        width: 600,
        fixed: "",
      },
      {
        title: "",
        dataIndex: "action",
        key: "action",
        width: 40,
        fixed: "",
      },
      {
        title: "Timeline",
        dataIndex: "timeline",
        key: "timeline",
      },
    ],
    [languageMap]
  );

  const detailColumnList = useMemo(
    () => [
      {
        title:
          languageMap?.["event.dashboard.myProject.timeline.name"] ?? "Name",
        dataIndex: "name",
        key: "name",
        // width: 600,
        fixed: "",
      },
      {
        title:
          languageMap?.["event.dashboard.myProject.timeline.assignee"] ??
          "Assignee",
        dataIndex: "assignee",
        key: "assignee",
        width: 120,
        fixed: "",
      },
      {
        title:
          languageMap?.["event.dashboard.myProject.timeline.status"] ??
          "Status",
        dataIndex: "status",
        key: "status",
        width: 250,
        fixed: "",
      },
      {
        title:
          languageMap?.["event.dashboard.myProject.timeline.startDate"] ??
          "Start date",
        dataIndex: "startDate",
        key: "startDate",
        width: 120,
        fixed: "",
      },
      {
        title:
          languageMap?.["event.dashboard.myProject.timeline.endDate"] ??
          "End date",
        dataIndex: "endDate",
        key: "endDate",
        width: 130,
        fixed: "",
      },
      {
        title:
          languageMap?.["modal.sideBarTimeLine.progressRate"] ??
          "Progress rate",
        dataIndex: "progressRate",
        key: "progressRate",
        width: 130,
        fixed: "",
      },
      {
        title: "",
        dataIndex: "action",
        key: "action",
        width: 40,
        fixed: "",
      },
    ],
    [languageMap]
  );

  const onDragEnd = async ({ active, over }) => {
    if (!permissionCurrentUser?.includes("U") || !active || !over) {
      return;
    }

    if (active?.id !== over?.id) {
      const activeTask = unwindTaskList?.find(
        (task) => task?.taskId === active?.id
      );
      const overTask = unwindTaskList?.find(
        (task) => task?.taskId === over?.id
      );

      const activeTaskCode = activeTask?.taskCode;
      const overTaskCode = overTask?.taskCode;

      if (activeTaskCode && overTaskCode) {
        setIsLoading(true);

        try {
          const result = await apiFactory.taskApi.swap({
            activeTaskCode,
            overTaskCode,
          });

          setIsLoading(false);

          if (result?.status !== 200) {
            return;
          }

          setDataSource((prev) => {
            const activeIndex = prev?.findIndex((i) => i?.key === active?.id);
            const overIndex = prev?.findIndex((i) => i?.key === over?.id);
            return arrayMove(prev, activeIndex, overIndex);
          });

          setData((prev) => {
            const activeIndex = prev?.findIndex(
              (i) => i?.taskId === active?.id
            );
            const overIndex = prev?.findIndex((i) => i?.taskId === over?.id);
            return arrayMove(prev, activeIndex, overIndex);
          });
        } catch (error) {}
      }
    }
  };

  const onDragOver = ({ active, over }) => {
    if (!permissionCurrentUser?.includes("U")) {
      return;
    }

    const activeIndex = columns?.findIndex((i) => i?.key === active?.id);
    const overIndex = columns?.findIndex((i) => i?.key === over?.id);

    if (activeIndex === -1 || overIndex === -1) return;

    setDragIndex({
      active: active?.id,
      over: over?.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  const handleResize =
    (column) =>
    (e, { size }) => {
      const a = columns.map((item) => {
        if (item?.dataIndex === column.dataIndex) {
          item.width = size.width;
        }
        return item;
      });

      setColumns([...a]);
    };

  const getDaysInMonth = (month, year) => {
    const daysInMonth = [];
    let currentDate = moment(`${year}-${month}-01`);
    const endDate = currentDate.clone().endOf("month");

    while (currentDate.isSameOrBefore(endDate)) {
      daysInMonth.push(currentDate.clone()); // Clone the date to avoid mutation
      currentDate.add(1, "day");
    }

    return daysInMonth;
  };

  const setIdForTabNav = () => {
    if (tableRef.current) {
      const tabNavElement = tableRef.current.querySelector(".ant-table-header");

      if (tabNavElement) {
        tabNavElement.id = "ant-table-header";
      }
    }
  };

  const weekendColor = (date) => {
    const day = date.format("dddd").substring(0, 3);
    if (["Sat", "Sun"].includes(day)) {
      return "red";
    } else {
      return "white";
    }
  };

  const isToday = (time) => {
    switch (timelineType) {
      case "date":
      case "week":
        if (time.format("DD/MM/YYYY") === moment().format("DD/MM/YYYY")) {
          return "#e5961f";
        }
        break;
      case "month":
        if (time === moment().format("MM/YYYY")) {
          return "#e5961f";
        }
        break;

      default:
        break;
    }
  };

  const generatePoint = (startDate, endDate) => {
    const timeline = [];
    let currentDate = moment(startDate, "YYYY-MM-DD");

    const end = moment(endDate, "YYYY-MM-DD");

    while (currentDate <= end) {
      timeline.push(currentDate.format("DD/MM/YYYY"));
      currentDate.add(1, "days");
    }

    return timeline;
  };

  const generateMonthYearList = (numberOfMonth) => {
    const monthYearList = [];
    const today = moment();

    const startMonth = today
      .clone()
      .subtract(numberOfMonth, "months")
      .startOf("month");
    const endMonth = today.clone().add(numberOfMonth, "months").endOf("month");

    let currentMonth = startMonth.clone();

    while (currentMonth.isSameOrBefore(endMonth, "month")) {
      monthYearList.push(currentMonth.format("MM/YYYY"));
      currentMonth.add(1, "month");
    }

    return monthYearList;
  };

  const generateDayInMonthOnHeader = (month, year) => {
    const dayList = getDaysInMonth(month, year);
    const color = month % 2 === 0 ? "#0091ff" : "#4db74d";
    return (
      <div className="calender-header" style={{ backgroundColor: `${color}` }}>
        <div className="text-[15px]">
          {year}/{month}
        </div>
        <div className="flex">
          {dayList?.map((e) => (
            <div
              key={e.format("DD/MM/YYYY")}
              className="p-[5px] w-[30px] text-center"
              style={{
                color: `${weekendColor(e)}`,
                backgroundColor: `${isToday(e)}`,
              }}
            >
              {e.format("DD")}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const generateWeekInMonth = (month, year) => {
    const monthColor = month % 2 === 0 ? "#0091ff" : "#4db74d";
    const weekColor = () => {
      numberOfWeek += 1;
      return numberOfWeek % 2 === 0 ? "#c9c93a" : "#2fc1bf";
    };
    const weeksInMonth = [];
    let currentDate = moment(`${year}-${month}-01`).startOf("week");
    const endDate = moment(`${year}-${month}-01`).endOf("month").endOf("week");

    while (currentDate.isSameOrBefore(endDate)) {
      const week = [];

      for (let i = 0; i < 7; i++) {
        if (currentDate.format("MM/YYYY") === `${month}/${year}`) {
          week.push(currentDate.clone());
        }
        currentDate.add(1, "day");
      }

      weeksInMonth.push(week);
    }

    return (
      <div
        className="calender-header"
        style={{ backgroundColor: `${monthColor}` }}
      >
        <div className="text-[15px]">
          {month}/{year}
        </div>
        <div className="flex">
          {weeksInMonth?.map((w, i) => {
            const wColor = weekColor();
            return (
              <div className="flex flex-col" key={i}>
                <div
                  className="p-[5px] text-center"
                  style={{ backgroundColor: `${wColor}` }}
                >
                  week {i}
                </div>
                <div className="flex">
                  {w?.map((d) => {
                    const day = d.format("dddd").substring(0, 3);
                    if (["Sat", "Sun"].includes(day)) {
                      return (
                        <div
                          key={d.format("DD/MM/YYYY")}
                          className="p-[5px] w-[45px] text-center text-[red]"
                          style={{ backgroundColor: `${wColor}` }}
                        >
                          {day}
                        </div>
                      );
                    } else {
                      return (
                        <div
                          key={d.format("DD/MM/YYYY")}
                          className="p-[5px] w-[45px] text-center"
                          style={{ backgroundColor: `${wColor}` }}
                        >
                          {day}
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const generateMonth = (month, year) => {
    const color = month % 2 === 0 ? "#e5961f" : "#4db74d";
    return (
      <div className="calender-header" style={{ backgroundColor: `${color}` }}>
        <div className="text-[15px] w-[150px] text-center">
          {month}/{year}
        </div>
      </div>
    );
  };

  const generateDayProgressPoint = (month, year, progress, itemWidth) => {
    const dayList = getDaysInMonth(month, year);
    return (
      <div className="calender-header">
        <div className="flex">
          {dayList?.map((e) => {
            if (progress?.includes(e.format("DD/MM/YYYY"))) {
              return (
                <div
                  key={e.format("DD/MM/YYYY")}
                  className="text-center bg-[#6a6ab3] flex justify-center"
                  style={{ width: `${itemWidth}px` }}
                >
                  <div
                    className="w-[3px] h-[25px]"
                    style={{ backgroundColor: `${isToday(e)}` }}
                  />
                </div>
              );
            } else {
              return (
                <div
                  key={e.format("DD/MM/YYYY")}
                  className="flex justify-center"
                  style={{ width: `${itemWidth}px` }}
                >
                  <div
                    className="w-[3px] h-[25px]"
                    style={{ backgroundColor: `${isToday(e)}` }}
                  />
                </div>
              );
            }
          })}
        </div>
      </div>
    );
  };

  const generateMonthProgressPoint = (monthYear, monthYearProgress) => {
    if (monthYearProgress?.includes(monthYear)) {
      return (
        <div
          key={monthYear}
          className="calender-header month-point bg-[#6a6ab3]"
        >
          <div
            className="w-[3px] h-[25px]"
            style={{ backgroundColor: `${isToday(monthYear)}` }}
          />
        </div>
      );
    } else {
      return (
        <div key={monthYear} className="calender-header month-point">
          <div
            className="w-[3px] h-[25px]"
            style={{ backgroundColor: `${isToday(monthYear)}` }}
          />
        </div>
      );
    }
  };

  const generateDayProgressPointInYear = (amplitude, progress) => {
    return generateMonthYearList(amplitude)?.map((e) => {
      return generateDayProgressPoint(
        e.split("/")[0],
        e.split("/")[1],
        progress,
        30
      );
    });
  };

  const generateWeekProgressPointInYear = (amplitude, progress) => {
    return generateMonthYearList(amplitude)?.map((e) => {
      return generateDayProgressPoint(
        e.split("/")[0],
        e.split("/")[1],
        progress,
        45
      );
    });
  };

  const generateMonthProgressPointInYear = (progress) => {
    const uniqProgressMonth = _.uniq(progress?.map((e) => e.substring(3, 10)));

    return generateMonthYearList(12)?.map((e) => {
      return generateMonthProgressPoint(e, uniqProgressMonth);
    });
  };

  const generateTimelineHeader = (dataIndex, title) => {
    if (dataIndex !== "timeline") {
      return (
        <div className="h-[50px] flex flex-col justify-center items-center">
          {title}
        </div>
      );
    }

    switch (timelineType) {
      case "date":
        return (
          <div className="flex">
            {generateMonthYearList(amplitude)?.map((e) => {
              return generateDayInMonthOnHeader(
                e.split("/")[0],
                e.split("/")[1]
              );
            })}
          </div>
        );
      case "week":
        return (
          <div className="flex">
            {generateMonthYearList(amplitude)?.map((e) => {
              return generateWeekInMonth(e.split("/")[0], e.split("/")[1]);
            })}
          </div>
        );
      case "month":
        return (
          <div className="flex">
            {generateMonthYearList(amplitude)?.map((e) => {
              return generateMonth(e.split("/")[0], e.split("/")[1]);
            })}
          </div>
        );

      default:
        break;
    }
  };

  const generateTimelineRow = (progress) => {
    switch (timelineType) {
      case "date":
        return (
          <div className="flex">
            {generateDayProgressPointInYear(amplitude, progress)}
          </div>
        );
      case "week":
        return (
          <div className="flex">
            {generateWeekProgressPointInYear(amplitude, progress)}
          </div>
        );
      case "month":
        return (
          <div className="flex">
            {generateMonthProgressPointInYear(progress)}
          </div>
        );

      default:
        break;
    }
  };

  const debounceChangDate = debounce((dateString, type, item, date) => {
    changeDate(dateString, type, item, date);
  }, 500);

  const changeDate = async (dateString, type, item, date) => {
    const parentKeyList = item?.path?.split("|");
    let task = findTask(data, parentKeyList);

    let unwindTask = unwindTaskList?.find(
      (unwindTask) => unwindTask?.taskId === item?.taskId
    );

    if (type === "startDate") {
      if (dayjs(date).isAfter(task?.endDate)) {
        return;
      }

      const startDate = !date
        ? null
        : moment(dateString, "YYYY/MM/DD").format("YYYY-MM-DD");
      task.startDate = startDate;

      unwindTask.startDate = startDate;
    } else {
      if (dayjs(date).isBefore(task?.startDate)) {
        return;
      }

      const endDate = !date
        ? null
        : moment(dateString, "YYYY/MM/DD").format("YYYY-MM-DD");
      task.endDate = endDate;
      unwindTask.endDate = endDate;
    }

    if (task?.taskCode && !isLoading) {
      setIsLoading(true);

      try {
        const result = await apiFactory.taskApi.store(task);

        if (result?.status !== 200) {
          setIsLoading(false);
          return;
        }

        setIsLoading(false);
        task.taskCode = result?.data?.taskCode;
        unwindTask.taskCode = result?.data?.taskCode;
      } catch (error) {
        setIsLoading(false);
        openNotificationError("failed to store task");
        return;
      }
    }

    setData([...data]);
    setUnwindTaskList([...unwindTaskList]);
  };

  const generateParticipant = (taskMemberList, countItem, item) => {
    return (
      <div className="flex justify-center">
        {taskMemberList?.map((member, index) => {
          const maxVisible = Math.min(taskMemberList?.length, countItem);
          const isHoverDiv =
            index === maxVisible - 1 && taskMemberList?.length > 0;

          return (
            <div className="relative group" key={member?.userCode}>
              {index < maxVisible - 1 ? (
                <div className="mr-2">
                  <Tooltip title={member?.name} key={member?.userCode}>
                    <div style={{ zoom: 0.8 }}>
                      <CustomAvatar person={member} pos={-1} styleCss={{}} />
                    </div>
                  </Tooltip>
                </div>
              ) : (
                isHoverDiv && (
                  <button
                    className="relative group cursor-pointer"
                    onClick={() => handleRemoveMember(item)}
                  >
                    <Tooltip
                      title={
                        languageMap?.["project.task.assignee.hover"] ??
                        "Show assignee"
                      }
                      key={member?.userCode}
                    >
                      <div
                        className="group-hover:border-1 blur-[1px] group-hover:border-black group-hover:blur-0"
                        style={{
                          zoom: 0.8,
                        }}
                      >
                        <CustomAvatar
                          person={member}
                          pos={-1}
                          styleCss={{}}
                        />
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-50 group-hover:opacity-100 transition duration-300">
                        <EllipsisOutlined
                          style={{ fontSize: "25px", color: "white" }}
                        />
                      </div>
                    </Tooltip>
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const changeProgressRate = (e, item) => {
    let task = unwindTaskList?.find(
      (unwindTask) => unwindTask?.taskId === item?.taskId
    );

    if (e?.target?.value >= 0 && e?.target?.value <= 100) {
      task.progressRate = e?.target?.value;
    } else if (e?.target?.value < 0) {
      item.progressRate = 0;
    } else if (e?.target?.value > 100) {
      item.progressRate = 100;
    }

    setUnwindTaskList([...unwindTaskList]);
  };

  const onBlurProgressRate = async (item, value) => {
    const parentKeyList = item?.path?.split("|");
    let task = findTask(data, parentKeyList);

    let unwindTask = unwindTaskList?.find(
      (unwindTask) => unwindTask?.taskId === item?.taskId
    );

    if (!task?.workflowCode) {
      const workflowCode = projectProgressList?.[0]?.projectProgressCode;
      task.workflowCode = workflowCode;

      if (unwindTask) {
        unwindTask.workflowCode = workflowCode;
      }
    }

    // taskName is mandatory
    if (!value) {
      task.progressRate = 0;
      unwindTask.progressRate = 0;
      return;
    }

    if (value < 0) {
      task.progressRate = 0;
      unwindTask.progressRate = 0;
      return;
    } else if (value > 100) {
      task.progressRate = 100;
      unwindTask.progressRate = 100;
      return;
    } else {
      task.progressRate = value;
      unwindTask.progressRate = value;
    }

    try {
      const result = await apiFactory.taskApi.store(task);

      if (result?.status !== 200) return;

      task.taskCode = result?.data?.taskCode;
      unwindTask.taskCode = result?.data?.taskCode;

      setUnwindTaskList([...unwindTaskList]);
      setData([...data]);
    } catch (error) {
      openNotificationError("failed to store task");
      return;
    }
  };

  const onBlurTask = async (item, value) => {
    const parentKeyList = item?.path?.split("|");
    let task = findTask(data, parentKeyList);

    let unwindTask = unwindTaskList?.find(
      (unwindTask) => unwindTask?.taskId === item?.taskId
    );

    if (!task?.workflowCode) {
      const workflowCode = projectProgressList?.[0]?.projectProgressCode;
      task.workflowCode = workflowCode;

      if (unwindTask) {
        unwindTask.workflowCode = workflowCode;
      }
    }

    // taskName is mandatory
    if (!value || value?.trim() === "") {
      return;
    }

    task.name = value;
    unwindTask.name = value;

    try {
      const result = await apiFactory.taskApi.store(task);

      if (result?.status !== 200 || !result?.data) return;

      task.taskCode = result?.data?.taskCode;
      unwindTask.taskCode = result?.data?.taskCode;

      setUnwindTaskList([...unwindTaskList]);
      setData([...data]);
    } catch (error) {
      openNotificationError("failed to store task");
      return;
    }
  };

  const onBlurStatus = async (workflowCode, item) => {
    const parentKeyList = item?.path?.split("|");
    let task = findTask(data, parentKeyList);
    task.workflowCode = workflowCode;

    if (task.taskCode) {
      try {
        const result = await apiFactory.taskApi.store(task);

        if (result?.status !== 200) return;

        task.taskCode = result?.data?.taskCode;
      } catch (error) {
        openNotificationError("failed to store task");
        return;
      }
    }

    setData([...data]);
  };

  const dataToDataSource = (itemList) => {
    return itemList?.map((item) => {
      const taskMemberList = item?.taskMemberDtoList || [];

      const showAddMoreSubTask = () => {
        if (!item?.path || item?.path.trim().length === 0) {
          return false;
        }

        const count = item?.path.split("|").length;

        return count < 4;
      };

      return {
        ...item,
        key: item?.path,
        name: (
          <div className="flex w-full task-text-item">
            {item?.taskCode ? (
              item?.name
            ) : (
              <TaskNameInput
                languageMap={languageMap}
                item={item}
                onBlurName={onBlurTask}
                isMobile={isMobile}
              />
            )}
          </div>
        ),
        assignee: generateParticipant(taskMemberList, isMobile ? 5 : 2, item),
        status: (
          <StatusSelect
            item={item}
            options={projectProgressList}
            onChangeStatus={onBlurStatus}
            disabled={!permissionCurrentUser?.includes("U")}
          />
        ),
        startDate: (
          <DateInput
            item={item}
            type="startDate"
            placeholder="Start Time"
            onChangeDate={debounceChangDate}
            disabled={!permissionCurrentUser?.includes("U")}
          />
        ),
        endDate: (
          <DateInput
            item={item}
            type="endDate"
            placeholder="End Time"
            onChangeDate={debounceChangDate}
            disabled={!permissionCurrentUser?.includes("U")}
          />
        ),
        progressRate: (
          <div
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <ProgressInput
              item={item}
              onChangeProgress={onBlurProgressRate}
              disabled={!permissionCurrentUser?.includes("U")}
            />
          </div>
        ),
        action: (
          <div className="flex gap-[10px] justify-center">
            {item?.taskCode && showAddMoreSubTask() && (
              <button
                className="cursor-pointer "
                hidden={!permissionCurrentUser?.includes("C")}
                onClick={() => addMoreSubTask(item?.path)}
              >
                <IoIosAddCircle color="#4db74d" size={25} />
              </button>
            )}
            {!item?.taskCode && (
              <button
                className="cursor-pointer "
                hidden={!permissionCurrentUser?.includes("D")}
                onClick={() => deleteTask(item?.path)}
              >
                <MdDelete color="#f00" size={25} />
              </button>
            )}
          </div>
        ),
        timeline: generateTimelineRow(
          generatePoint(item?.startDate, item?.endDate)
        ),
        children: item?.children?.length && [
          ...dataToDataSource(item?.children),
        ],
      };
    });
  };

  const getUnwindTaskList = (unwindTasks, taskTree) => {
    for (const task of taskTree) {
      unwindTasks?.push(task);

      if (task?.children?.length) {
        getUnwindTaskList(unwindTasks, task?.children);
      }
    }
  };

  const calculateTimelineListHeight = () => {
    const newTimelineListHeight =
      window.innerHeight -
      headerDiv?.current?.clientHeight -
      document?.getElementById("header")?.offsetHeight -
      document?.getElementById("ant-table-header")?.offsetHeight -
      60;

    const thElements = document.querySelectorAll(".ant-table-thead tr th");

    const thWidths = Array.from(thElements)
      .slice(0, 6)
      .reduce((sum, th) => sum + th.offsetWidth, 0);

    const elementWidth =
      document?.getElementById("work-management-container")?.offsetWidth +
      document?.getElementById("sidebar-layout-element")?.offsetWidth;

    if (window.innerWidth > thWidths + elementWidth) {
      // setIsShowTimeline(true);
    } else {
      // setIsShowTimeline(false);
    }

    setTimelineHeight(newTimelineListHeight);
  };

  const scrollToPosition = (length) => {
    if (viewMode === PROJECT_VIEW_MODE.TIMELINE && tableRef.current) {
      tableRef.current.querySelector(".ant-table-body").scrollLeft = length;
    }
  };

  const addMoreTask = () => {
    const uid = uuidv4();

    const newData = {
      key: uid,
      task: "",
      manager: "",
      status: "",
      startDate: "",
      endDate: "",
      path: uid,
      taskId: uid,
      projectCode: projectCode,
      isNewRecord: true,
    };

    setData((prev) => [...prev, newData]);

    delay(() => {
      if (tableRef.current) {
        tableRef.current.querySelector(".ant-table-body").scrollTop =
          tableRef.current.querySelector(".ant-table-body").scrollHeight + 100;
      }
    }, 200);

    const newTask = {
      key: uid,
      task: "",
      manager: "",
      status: "",
      startDate: "",
      endDate: "",
      path: uid,
      taskId: uid,
      projectCode: projectCode,
    };

    setUnwindTaskList((prev) => [...prev, newTask]);

    // handleSaveScroll();
  };

  const removeTask = (key) => {
    const parentKeyList = key?.split("|");
    let task = findTask(
      data,
      parentKeyList?.slice(0, parentKeyList?.length - 1)
    );
    const index = task?.children?.findIndex((i) => i?.path === key);
    if (index > -1) {
      task?.children?.splice(index, 1);
    }
  };

  const deleteTask = async (key) => {
    const parentKeyList = key?.split("|");
    let task = findTask(
      data,
      parentKeyList?.slice(0, parentKeyList?.length - 1)
    );
    const index = task?.children?.findIndex((i) => i?.path === key);

    if (index < 0) return;
    // if task existed then need to ask
    // else delete right away
    if (task?.children?.[index]?.taskCode) {
      setDeletionTask(task?.children?.[index]);
      setIsOpenDeleteModal(true);
    } else {
      const newUnwindTaskList = unwindTaskList?.filter(
        (unwindTask) => unwindTask?.taskId !== task?.children?.[index]?.taskId
      );
      setUnwindTaskList([...newUnwindTaskList]);
      task?.children?.splice(index, 1);

      // keepOldScrollTable();

      setData([...data]);
    }
  };

  const keepOldScrollTable = () => {
    const tableBody = tableRef?.current?.querySelector(".ant-table-body");

    if (tableBody) {
      tableBody.scrollTop = oldScrollY;
    }
  };

  const findTask = (tree, parentKeyList) => {
    let item = {
      children: tree,
    };
    forEach(parentKeyList, (key) => {
      item = item?.children?.find((t) => t?.taskId === key);
    });

    return item;
  };

  const addMoreSubTask = (key) => {
    const parentKeyList = key?.split("|");
    let item = findTask(data, parentKeyList);
    const uid = uuidv4();

    if (item?.children?.length) {
      item?.children?.push({
        path: item?.path + "|" + uid,
        taskId: uid,
        projectCode: item?.projectCode,
        parentCode: item?.taskCode,
      });
    } else {
      item.children = [
        {
          path: item?.path + "|" + uid,
          taskId: uid,
          projectCode: item?.projectCode,
          parentCode: item?.taskCode,
        },
      ];
    }

    setData([...data]);

    unwindTaskList?.push({
      path: item?.path + "|" + uid,
      taskId: uid,
      projectCode: item?.projectCode,
      parentCode: item?.taskCode,
    });

    expandedRowKeys?.push(item?.path);
    setExpandedRowKeys((prev) => [...prev, ...expandedRowKeys]);
    setUnwindTaskList([...unwindTaskList]);
  };

  const fetchTaskList = async (taskCode) => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      resetScrollTable();

      const result = await apiFactory.taskApi.getList({
        limit: MY_TASK.MY_TASK_LIMIT,
        direction: "ASC",
        search: {
          projectCode: projectCode,
          name: searchInputValue,
          taskCode: taskCode,
          sortSequence: null,
          workflowCode: statusFilter,
        },
      });

      if (result?.status === 200) {
        setSortSequence(
          result?.data?.items[result?.data?.items?.length - 1]?.sortSequence
        );

        if (result?.data?.items?.length < MY_TASK.MY_TASK_LIMIT) {
          setIsLoadMoreData(false);
        }

        if (result?.data?.items?.length === 0) {
          setIsLoading(false);
        }

        setData([...(result?.data?.items || [])]);
        fetchUnwindTasks([...(result?.data?.items || [])], false);
      } else {
        openNotificationError(result?.message);
        setIsLoading(false);
      }
    } catch (error) {}
  };

  const fetchUnwindTasks = (data, isMore) => {
    let unwindTasks = [];

    const addUnwindTasks = (tasks) => {
      if (!tasks?.length) return;

      tasks.forEach((task) => {
        unwindTasks.push(task);
        if (task?.children?.length) {
          addUnwindTasks(task.children);
        }
      });
    };

    addUnwindTasks(data);

    setUnwindTaskList((prev) =>
      isMore ? uniqBy([...prev, ...unwindTasks], "taskCode") : [...unwindTasks]
    );
  };

  const fetchProjectProgressList = async () => {
    const result = await apiFactory.projectProgressApi.getList(projectCode);

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      return;
    }

    setProjectProgressList(
      result?.data?.map((item) => ({
        ...item,
        value: item?.projectProgressCode,
        label: labelLanguage(user?.language, item),
      }))
    );
  };

  const labelLanguage = (language, item) => {
    switch (language) {
      case "kr":
        return item?.krName;
      case "us":
        return item?.enName;
      case "vn":
        return item?.vnName;
      default:
        return item?.enName;
    }
  };

  const fetchProjectDetails = async () => {
    const result = await apiFactory.projectApi.getProjectDetail(projectCode);

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      navigate(-1);
      return;
    }
    const permission = getUserPermissions(
      projectCode,
      AUTHORITY_CONFIG.TASK.ID,
      user?.userCode,
      result?.data.projectAuthorityDtos,
      result?.data.projectParticipantDtos
    );
    setProjectDetail(result?.data);
    setPermissionCurrentUser(permission);
  };

  const expandIcon = ({ expanded, onExpand, record }) => {
    if (record?.children?.length) {
      return expanded ? (
        <div>
          <CaretDownOutlined size={23} onClick={(e) => onExpand(record, e)} />
        </div>
      ) : (
        <div>
          <CaretRightOutlined size={23} onClick={(e) => onExpand(record, e)} />
        </div>
      );
    } else {
      return (
        <div>
          <CaretRightOutlined
            className="grey-btn"
            size={23}
            onClick={(e) => onExpand(record, e)}
          />
        </div>
      );
    }
  };

  const closeModalConfirm = () => {
    setIsOpenDeleteModal(false);
  };

  const handleOnConfirm = async () => {
    const parentKeyList = deletionTask?.path?.split("|");

    let task = findTask(
      data,
      parentKeyList?.slice(0, parentKeyList?.length - 1)
    );
    const index = task?.children?.findIndex(
      (i) => i?.path === deletionTask?.path
    );

    if (index < 0) return;

    const result = await apiFactory.taskApi.deleteTask(deletionTask?.path);

    if (result?.status === 200) {
      const newUnwindTasks = unwindTaskList?.filter(
        (unwindTask) => unwindTask?.taskId !== deletionTask?.taskId
      );
      setUnwindTaskList([...newUnwindTasks]);

      task?.children?.splice(index, 1);
      setData([...data]);
    } else {
      openNotificationError("Remove message failed");
    }

    setDeletionTask(null);
    closeModalConfirm();
    setIsOpenTimelineDetail(false);
    setSelectedTask(null);
  };

  const handleRowClick = (event, record) => {
    event?.preventDefault();
    let task = unwindTaskList?.find(
      (unwindTask) => unwindTask?.taskId === record?.taskId
    );

    setSelectedTask(task);
    setIsOpenTimelineDetail(true);
  };

  const handleAddMember = () => {
    setTypeMemberProjectModal("CREATE");
    setIsModalAddMember(true);
  };

  const handleRemoveMember = (item) => {
    const parentKeyList = item?.path?.split("|");
    let task = findTask(data, parentKeyList);
    setSelectedTask(task);
    setTypeMemberProjectModal("REMOVE");
    setIsModalAddMember(true);
  };

  const handleSubmitAddMember = async (memberList) => {
    setIsModalAddMember(false);

    if (memberList?.length > 0) {
      const parentKeyList = selectedTask?.path?.split("|");

      let task = findTask(data, parentKeyList);

      let unwindTask = unwindTaskList?.find(
        (unwindTask) => unwindTask?.taskId === task?.taskId
      );

      const taskMemberRequests = memberList?.map((member) => ({
        taskCode: task?.taskCode,
        memberCode: member?.code,
        sortSequence: "desc",
      }));

      setIsLoading(true);

      const result =
        await apiFactory.taskMemberApi.assignMember(taskMemberRequests);

      if (result?.status === 200) {
        openNotificationSuccess("Add member success");

        const memberCodes = result?.data?.map((item) => item?.userCode);

        const newTaskMemberDtoList = [
          ...(task.taskMemberDtoList?.filter(
            (item) => !memberCodes?.includes(item?.memberCode)
          ) || []),
          ...(result?.data || []),
        ];

        task.taskMemberDtoList = newTaskMemberDtoList;
        unwindTask.taskMemberDtoList = newTaskMemberDtoList;

        setData([...data]);
        setUnwindTaskList([...unwindTaskList]);
      } else {
        openNotificationError(result?.message);
        setIsLoading(false);
      }
    }
  };

  const handleSubmitRemoveMember = async (memberList) => {
    setIsModalAddMember(false);
    setIsLoading(true);

    try {
      if (memberList?.length > 0) {
        const parentKeyList = selectedTask?.path?.split("|");
        let task = findTask(data, parentKeyList);

        let unwindTask = unwindTaskList?.find(
          (unwindTask) => unwindTask?.taskId === task?.taskId
        );

        const taskMemberRequests = memberList?.map((member) => ({
          taskCode: task?.taskCode,
          memberCode: member?.code,
          sortSequence: "desc",
        }));

        const result =
          await apiFactory.taskMemberApi.removeAssignee(taskMemberRequests);

        if (result?.status === 200) {
          openNotificationSuccess("Remove member success");

          const memberCodes = result?.data?.map((item) => item?.memberCode);

          task.taskMemberDtoList = task?.taskMemberDtoList?.filter(
            (item) => !memberCodes.includes(item?.memberCode)
          );

          unwindTask.taskMemberDtoList = unwindTask?.taskMemberDtoList?.filter(
            (item) => !memberCodes.includes(item?.memberCode)
          );

          setData([...data]);
          setUnwindTaskList([...unwindTaskList]);
        } else {
          openNotificationError(result?.message);
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetIsSearching = () => {
    const isSearching = searchInputValue?.trim() !== "" || statusFilter;

    setIsSearching(isSearching);
  };

  const onChangeViewMode = () => {
    if (viewMode === PROJECT_VIEW_MODE.TIMELINE) {
      setColumns([
        ...detailColumnList.map((col, i) => {
          col.key = i;
          col.onHeaderCell = () => ({
            id: `${i}`,
            width: col.width,
            onResize: handleResize(col),
          });
          col.title = generateTimelineHeader(col.dataIndex, col.title);
          col.onCell = () => ({
            id: `${i}`,
            width: col.width,
            // onResize: handleResize(col),
          });
          return col;
        }),
      ]);

      setViewMode(PROJECT_VIEW_MODE.DETAIL);
    } else {
      setColumns([
        ...timelineColumnList.map((col, i) => {
          col.key = i;
          col.onHeaderCell = () => {
            return {
              id: `${i}`,
              width: col.width,
              onResize: handleResize(col),
            };
          };
          col.title = generateTimelineHeader(col.dataIndex, col.title);
          col.onCell = () => ({
            id: `${i}`,
            width: col.width,
            // onResize: handleResize(col),
          });

          if (i < 2) col.fixed = "left";

          return col;
        }),
      ]);

      setViewMode(PROJECT_VIEW_MODE.TIMELINE);
    }
  };

  useEffect(() => {
    handleSetIsSearching();
  }, [searchInputValue, statusFilter]);

  const debouncedFilter = debounce((event) => {
    handleRemoveChangeStyleTasks();
    setSearchInputValue(event?.target?.value);

    if (event?.target?.value === null) {
      setSortSequence(null);
    }
  }, 500);

  const handleRowsChange = (isOpen, rowKey) => {
    const updatedKeys = isOpen
      ? expandedRowKeys.push(rowKey)
      : expandedRowKeys.filter((key) => key !== rowKey);
    setExpandedRowKeys(updatedKeys);
  };

  const lastRecordRef = (node) => {
    if (!isLoadMoreData || isLoading) return;

    if (lastObserver.current) lastObserver.current.disconnect();

    lastObserver.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        setIsLoading(true);
        try {
          const result = await apiFactory.taskApi.getList({
            limit: 15,
            direction: "ASC",
            search: {
              projectCode: projectCode,
              name: searchInputValue,
              sortSequence: sortSequence,
              workflowCode: statusFilter,
            },
          });

          if (result?.status !== 200) {
            openNotificationError(result?.message);
            return;
          }

          setSortSequence(
            result?.data?.items[result?.data?.items?.length - 1]?.sortSequence
          );

          if (result?.data?.items?.length < 15) {
            setIsLoadMoreData(false);
          }

          setData((prev) =>
            uniqBy([...prev, ...(result?.data?.items || [])], "taskCode")
          );

          fetchUnwindTasks(result?.data?.items, true);
        } catch (error) {
          openNotificationError("fetch failed new tasks");
          setIsLoading(false);
        }
      }
    });

    if (node) lastObserver.current.observe(node);
  };

  const handleRemoveChangeStyleTasks = () => {
    setExpandedRowKeys([]);
    setRowChangeStylePaths([]);
  };

  const handleChangeStyleTasks = () => {
    if (!isSearching) return;

    if (unwindTaskList?.length > 0) {
      const regexSearchInput = new RegExp(searchInputValue?.trim(), "i");
      let newExpandedRowKeys = new Set();
      let newRowChangeStylePaths = [];

      unwindTaskList.forEach((unwindTask) => {
        const pathSplit = unwindTask?.path?.split("|");
        const lastPath = pathSplit?.[pathSplit?.length - 1];

        if (
          (searchInputValue?.trim() !== "" &&
            regexSearchInput.test(unwindTask?.name)) ||
          statusFilter === unwindTask?.workflowCode ||
          !unwindTask?.taskCode
        ) {
          newRowChangeStylePaths.push(lastPath);

          pathSplit?.forEach((path, index) => {
            if (index < pathSplit.length - 1) {
              const subArrayPath = pathSplit.slice(0, index + 1).join("|");
              newExpandedRowKeys.add(subArrayPath);
            }
          });
        }
      });

      setRowChangeStylePaths([...new Set(newRowChangeStylePaths)]);
      setExpandedRowKeys([...newExpandedRowKeys]);
    }
  };

  const handleCheckAddMoreTasks = () => {
    const hasNoTaskCode = unwindTaskList?.some((task) => !task?.taskCode);

    setIsNewRecord(hasNoTaskCode);
  };

  const handleStatusFilter = (value) => {
    if (!value) {
      setSortSequence(null);
      handleRemoveChangeStyleTasks();
    }

    setStatusFilter(value);
  };

  const handleSaveScroll = () => {
    setOldScrollY(
      tableRef?.current?.querySelector(".ant-table-body")?.scrollTop || 0
    );
  };

  const resetScrollTable = () => {
    setData([]);
    if (tableRef.current) {
      const tableBody = tableRef.current.querySelector(".ant-table-body");
      if (tableBody) {
        tableBody.scrollTop = 0;
      }
    }
  };

  const onClose = useCallback(() => {
    setIsOpenTimelineDetail(false);
    setSelectedTask(null);
  });

  useEffect(() => {
    if (unwindTaskList?.length === 0) return;

    if (searchParams?.get("type") === "COMMENT" && selectedTask) {
      /* open modal comment when handle click alarm type = COMMENT */
      let task = unwindTaskList[0];
      setSelectedTask(task);
      setIsOpenTimelineDetail(true);
    }
  }, [unwindTaskList, searchParams]);

  useEffect(() => {
    handleCheckAddMoreTasks();
  }, [unwindTaskList]);

  useEffect(() => {
    if (data?.length > 0) {
      handleChangeStyleTasks();
    }
  }, [data]);

  useEffect(() => {
    setIdForTabNav();
  }, []);

  useEffect(() => {
    calculateTimelineListHeight();

    window.addEventListener("resize", calculateTimelineListHeight);

    return () => {
      window.removeEventListener("resize", calculateTimelineListHeight);
    };
  }, []);

  useEffect(() => {
    if (permissionCurrentUser) {
      setDataSource([...dataToDataSource(data)]);

      if (data?.length !== 0) setIsLoading(false);
    }
  }, [timelineType, amplitude, permissionCurrentUser, data, isMobile]);

  useEffect(() => {
    if (viewMode === PROJECT_VIEW_MODE.DETAIL) return;

    let finalTodayTimelineLength = 0;
    let finalTimelineLength = 0;

    if (timelineType === "date") {
      const timeList = generateMonthYearList(amplitude)?.reduce(
        (accumulator, e) => {
          let newArray = accumulator?.concat(
            getDaysInMonth(e.split("/")[0], e.split("/")[1])
          );
          return newArray;
        },
        []
      );

      const timeToTodayList = timeList?.filter((time) =>
        time.isSameOrBefore(moment())
      );

      finalTodayTimelineLength = (timeToTodayList?.length - 3) * 30;
      finalTimelineLength = timeList?.length * 30 + expandTinelineLength;
    }

    if (timelineType === "week") {
      const timeList = generateMonthYearList(amplitude)?.reduce(
        (accumulator, e) => {
          let newArray = accumulator?.concat(
            getDaysInMonth(e.split("/")[0], e.split("/")[1])
          );
          return newArray;
        },
        []
      );

      const timeToTodayList = timeList?.filter((time) =>
        time.isSameOrBefore(moment())
      );

      finalTodayTimelineLength = (timeToTodayList?.length - 3) * 45;
      finalTimelineLength = timeList?.length * 45 + expandTinelineLength;
    }

    if (timelineType === "month") {
      const timeList = generateMonthYearList(amplitude);
      const thisMonth = moment().format("MM/YYYY");
      const timeToTodayList = timeList?.filter((time) =>
        moment(time, "MM/YYYY").isSameOrBefore(moment(thisMonth, "MM/YYYY"))
      );

      finalTodayTimelineLength = (timeToTodayList?.length - 1) * 150;

      finalTimelineLength = timeList?.length * 150 + expandTinelineLength;
    }

    setTodayTimelineLength(finalTodayTimelineLength);
    setTimelineLength(finalTimelineLength);

    //have to wait to render UI before scroll to today
    _.delay(() => scrollToPosition(finalTodayTimelineLength), 100);
  }, [timelineType, amplitude, viewMode]);

  useEffect(() => {
    if (viewMode === PROJECT_VIEW_MODE.DETAIL)
      setColumns(
        detailColumnList.map((col, i) => {
          col.key = `${i}`;
          col.onHeaderCell = () => {
            return {
              id: `${i}`,
              width: col.width,
              onResize: handleResize(col),
            };
          };
          col.title = generateTimelineHeader(col.dataIndex, col.title);
          col.onCell = () => ({
            id: `${i}`,
            width: col.width,
            // onResize: handleResize(col),
          });

          return col;
        })
      );
  }, [timelineType, amplitude]);

  useEffect(() => {
    fetchProjectProgressList();
  }, [projectCode]);

  useEffect(() => {
    if (projectProgressList?.length > 0) {
      setProjectProgressListSelect([
        {
          value: null,
          label:
            languageMap?.["project.management.timeline.allStatuses"] ??
            "All statuses",
        },
        ...projectProgressList.map((item) => ({
          ...item,
          value: item?.projectProgressCode,
          label: labelLanguage(user?.language, item),
        })),
      ]);

      setData([...data]);
    }
  }, [projectProgressList]);

  useEffect(() => {
    if (projectProgressList?.length > 0) {
      const updatedList = projectProgressList?.map((item) => ({
        ...item,
        label: labelLanguage(user?.language, item),
      }));
      setProjectProgressList(updatedList);
    }
  }, [user?.language]);

  useEffect(() => {
    let taskCode = searchParams?.get("task-code");

    if (searchInputValue?.trim() !== "" || statusFilter) {
      let newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("task-code");
      setSearchParams(newSearchParams);
      taskCode = null;
    }

    setIsLoadMoreData(true);

    if (taskCode === "0") {
      fetchTaskList(null);
    } else {
      fetchTaskList(taskCode);
    }
  }, [searchParams, projectCode, searchInputValue, statusFilter]);

  useEffect(() => {
    if (user?.userId && projectCode) {
      fetchProjectDetails();
    }
  }, [user?.userId, projectCode]);

  return (
    <div className="timeline p-[5px] overflow-y-hidden">
      <div
        ref={headerDiv}
        className={`${isMobile ? "absolute top-[0] left-[0] right-[0] p-2 z-[100] bg-white " : ""}`}
      >
        <div className="title ">
          {!isWorkManagementOptions && (
            <a
              className="btn-back-to-work-management"
              onClick={switchIsWorkManagementOptions}
            >
              <LeftOutlined size={25} />
            </a>
          )}
          <span>{projectDetail?.name}</span>
        </div>
        <div className="flex justify-between gap-[20px]" id="header">
          <div className="py-2 flex gap-[10px] flex-wrap">
            <Input
              placeholder={
                languageMap?.["menu.search.timeline.placeholder"] ??
                "Search task name"
              }
              onChange={(event) => {
                debouncedFilter(event);
              }}
              prefix={<IoSearchOutline size={15} />}
              allowClear
              className="min-w-[300px] flex-1"
            />

            <Select
              value={statusFilter}
              className={`${isMobile ? "min-w-[50%]" : "min-w-[250px]"}  flex-1`}
              options={projectProgressListSelect}
              onChange={(value) => handleStatusFilter(value)}
            />

            <Button
              type="primary"
              onClick={addMoreTask}
              disabled={
                !permissionCurrentUser?.includes("C") ||
                isNewRecord ||
                isLoading
              }
            >
              {languageMap?.["event.dashboard.myProject.timeline.addTask"] ??
                "Add more task +"}
            </Button>
          </div>
          {viewMode === PROJECT_VIEW_MODE.TIMELINE && (
            <div className="py-2 flex gap-[10px]">
              <Button onClick={() => scrollToPosition(todayTimelineLength)}>
                {languageMap?.["event.dashboard.myProject.timeline.today"] ??
                  "Today"}
              </Button>
              <Select
                value={timelineType}
                className="w-[120px]"
                onChange={setTimelineType}
                options={[
                  {
                    value: "date",
                    label:
                      languageMap?.[
                        "event.dashboard.myProject.timeline.date"
                      ] ?? "Date",
                  },
                  {
                    value: "week",
                    label:
                      languageMap?.[
                        "event.dashboard.myProject.timeline.week"
                      ] ?? "Week",
                  },
                  {
                    value: "month",
                    label:
                      languageMap?.[
                        "event.dashboard.myProject.timeline.month"
                      ] ?? "Month",
                  },
                ]}
              />
              <Select
                value={amplitude}
                className="w-[120px]"
                onChange={setAmplitude}
                options={[
                  {
                    value: 3,
                    label:
                      languageMap?.[
                        "event.dashboard.myProject.timeline.amplitude.threeMonth  "
                      ] ?? "3 month",
                  },
                  {
                    value: 6,
                    label:
                      languageMap?.[
                        "event.dashboard.myProject.timeline.amplitude.sixMonth"
                      ] ?? "6 month",
                  },
                  {
                    value: 12,
                    label:
                      languageMap?.[
                        "event.dashboard.myProject.timeline.amplitude.oneYear"
                      ] ?? "1 year",
                  },
                ]}
              />
            </div>
          )}
        </div>
        {!isMobile && (
          <div>
            <Radio.Group
              onChange={onChangeViewMode}
              value={viewMode}
              options={[
                {
                  value: PROJECT_VIEW_MODE.DETAIL,
                  label: <BiDetail className="mt-[7px]" size={23} />,
                },
                {
                  value: PROJECT_VIEW_MODE.TIMELINE,
                  label: <MdOutlineTimeline className="mt-[7px]" size={25} />,
                },
              ]}
            />
          </div>
        )}
      </div>

      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={dataSource.map((i) => i.key)}
          strategy={verticalListSortingStrategy}
        >
          <DragIndexContext.Provider value={dragIndex}>
            <div
              className={`relative ${isMobile ? "mt-[130px] overflow-auto" : ""}`}
            >
              {isMobile ? (
                <MobileCardTable
                  columns={columns}
                  dataSource={dataSource}
                  handleRowClick={handleRowClick}
                  addMoreSubTask={addMoreSubTask}
                  permissionCurrentUser={permissionCurrentUser}
                  deleteTask={deleteTask}
                />
              ) : (
                <div ref={tableRef}>
                  <Table
                    className="custom-table-scroll"
                    bordered
                    rowKey="key"
                    columns={columns}
                    dataSource={dataSource}
                    components={
                      permissionCurrentUser?.includes("U") && {
                        header: {
                          cell: TableHeaderCell,
                        },
                        body: {
                          row: Row,
                        },
                      }
                    }
                    scroll={{
                      x:
                        viewMode === PROJECT_VIEW_MODE.TIMELINE &&
                        timelineLength,
                      y: timelineHeight,
                    }}
                    pagination={false}
                    expandable={{
                      expandedRowKeys: expandedRowKeys,
                      onExpandedRowsChange: setExpandedRowKeys,
                      expandIcon: expandIcon,
                      onExpand: (expanded, record) => {
                        handleRowsChange(expanded, record.key);
                      },
                    }}
                    onRow={(record, index) => ({
                      onDoubleClick: (e) => handleRowClick(e, record),
                      className: `
                      ${record?.taskId === selectedTask?.taskId ? " highlighted-row-clicked " : ""}
                      ${rowChangeStylePaths?.some((path) => path === record?.taskId) ? " highlighted-row " : ""}
                      ${record?.isNewRecord ? " highlighted-new-row " : ""}
                    `.trim(),
                      ref:
                        index === dataSource?.length - 1 ? lastRecordRef : null,
                    })}
                    loading={{
                      spinning: isLoading,
                      indicator: (
                        <Spin
                          indicator={<LoadingOutlined spin />}
                          size="large"
                        />
                      ),
                      delay: 0,
                    }}
                    rowClassName="dashboard-row"
                  />
                </div>
              )}
            </div>
          </DragIndexContext.Provider>
        </SortableContext>
      </DndContext>

      {isOpenDeleteModal && (
        <GeneralModal
          title={
            (languageMap?.["modal.confirm.deleteTask"] ??
              "Are you sure to delete task: ") + deletionTask?.name
          }
          onCancel={closeModalConfirm}
          open={isOpenDeleteModal}
          onConfirm={handleOnConfirm}
        />
      )}
      {isOpenTimelineDetail && selectedTask?.taskCode && (
        <SideBarTimeline
          onClose={onClose}
          isOpen={isOpenTimelineDetail}
          data={selectedTask}
          allDataTimeline={data}
          unwindTaskList={unwindTaskList}
          setUnwindTaskList={setUnwindTaskList}
          projectProgressList={projectProgressList}
          changeDate={changeDate}
          onBlurTask={onBlurTask}
          changeProgressRate={changeProgressRate}
          onBlurProgressRate={onBlurProgressRate}
          onBlurStatus={onBlurStatus}
          handleAddMember={handleAddMember}
          deleteTask={deleteTask}
          permissionCurrentUser={permissionCurrentUser}
          generateParticipant={generateParticipant}
          currentProjectDetail={projectDetail}
        />
      )}
      {isModalAddMember && (
        <MemberProjectModal
          isOpen={isModalAddMember}
          onClose={() => setIsModalAddMember(false)}
          onCreate={handleSubmitAddMember}
          onRemove={handleSubmitRemoveMember}
          selectedTask={selectedTask}
          type={typeMemberProjectModal}
          permission={permissionCurrentUser}
        />
      )}
    </div>
  );
};

export { Timeline };
